import React, { useState } from "react";
import "./MasterTable.css";

// ─── Helpers ────────────────────────────────────────────────────────────────

const truncate = (str, max = 20) => {
  if (!str) return "—";
  const s = String(str);
  return s.length > max ? s.slice(0, max) + "…" : s;
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const Badge = ({ value, colorMap }) => {
  if (!value) return <span className="master-table-empty">—</span>;
  const color = colorMap?.[value] || "default";
  return (
    <span className={`master-table-badge master-table-badge--${color}`}>
      {value}
    </span>
  );
};

const CellContent = ({ col, value, row }) => {
  const [hovered, setHovered] = useState(false);

  if (col.render) {
    return col.render(value, row);
  }

  if (col.type === "badge") {
    return <Badge value={value} colorMap={col.colorMap} />;
  }

  if (col.truncate !== false) {
    const maxLen = col.maxLen || 20;
    const raw = value != null ? String(value) : "—";
    const isTruncated = raw.length > maxLen;
    const display = isTruncated ? truncate(raw, maxLen) : raw;

    return (
      <span
        className="master-table-cell-text"
        onMouseEnter={() => isTruncated && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ position: "relative" }}
      >
        {display}
        {hovered && isTruncated && (
          <span className="master-table-tooltip">{raw}</span>
        )}
      </span>
    );
  }

  return <span className="master-table-cell-text">{value ?? "—"}</span>;
};

// ─── MasterTable ─────────────────────────────────────────────────────────────

/**
 * MasterTable
 * Pure presentation table component with sticky first column, horizontal scrolling,
 * custom badges, skeleton loading, and pagination.
 *
 * @param {object[]}  columns         - Column definitions
 * @param {object[]}  data            - Row data (already filtered by page)
 * @param {string}    [emptyMessage]  - Text when no rows
 * @param {boolean}   [loading]       - Show skeleton loader
 * @param {number}    [pageSize]      - Rows per page (0 = no pagination)
 * @param {string}    [tableNote]     - Note shown below table
 * @param {function}  [onRowClick]    - Row click handler
 * @param {string}    [rowKey]        - Field used as row key (default "id")
 */
const MasterTable = ({
  columns = [],
  data = [],
  emptyMessage = "No data found.",
  loading = false,
  pageSize = 0,
  tableNote,
  onRowClick,
  rowKey = "id",
}) => {
  const [page, setPage] = useState(1);

  // ─── Derived ────────────────────────────────────────────────────────────

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(data.length / pageSize)) : 1;
  const safePage = Math.min(page, totalPages);
  const rows =
    pageSize > 0
      ? data.slice((safePage - 1) * pageSize, safePage * pageSize)
      : data;

  // Split: first column is "sticky", rest are scrollable
  const [firstCol, ...restCols] = columns;

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="master-table-root">
      {/* ── Table wrapper ───────────────────────────────────── */}
      <div className="master-table-scroll-container">
        <table className="master-table">
          <thead className="master-table-thead">
            <tr>
              {/* Fixed first column header */}
              {firstCol && (
                <th
                  className="master-table-th master-table-th--fixed"
                  style={{ width: firstCol.width || 160 }}
                >
                  {firstCol.label}
                </th>
              )}
              {/* Scrollable remaining headers */}
              {restCols.map((col) => (
                <th
                  key={col.accessor || col.label}
                  className="master-table-th"
                  style={{ width: col.width || 180, minWidth: col.minWidth || 140 }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="master-table-tbody">
            {loading ? (
              Array.from({ length: 6 }).map((_, ri) => (
                <tr key={`skel-${ri}`} className="master-table-tr master-table-tr--skeleton">
                  {columns.map((col) => (
                    <td
                      key={col.accessor || col.label}
                      className={`master-table-td ${ri === 0 ? "master-table-td--fixed" : ""}`}
                    >
                      <span className="master-table-skeleton-line" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="master-table-empty-cell"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={row[rowKey] ?? ri}
                  className={`master-table-tr ${onRowClick ? "master-table-tr--clickable" : ""}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {/* Fixed first cell */}
                  {firstCol && (
                    <td
                      className="master-table-td master-table-td--fixed"
                      style={{ width: firstCol.width || 160 }}
                    >
                      <CellContent col={firstCol} value={row[firstCol.accessor]} row={row} />
                    </td>
                  )}
                  {/* Scrollable cells */}
                  {restCols.map((col) => (
                    <td
                      key={col.accessor || col.label}
                      className="master-table-td"
                      style={{ width: col.width || 180, minWidth: col.minWidth || 140 }}
                    >
                      <CellContent col={col} value={row[col.accessor]} row={row} />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Footer ──────────────────────────────────────────── */}
      {(tableNote || (pageSize > 0 && totalPages > 1)) && (
        <div className="master-table-footer">
          {tableNote && (
            <div className="master-table-note">
              <span className="master-table-note-icon">ℹ</span>
              {tableNote}
            </div>
          )}

          {pageSize > 0 && totalPages > 1 && (
            <div className="master-table-pagination">
              <button
                className="master-table-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                type="button"
              >
                ‹
              </button>
              <span className="master-table-page-info">
                {safePage} / {totalPages}
              </span>
              <button
                className="master-table-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                type="button"
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterTable;
